import { useFormikContext } from 'formik';

import { FormSection } from '@@/form-components/FormSection';
import { SwitchField } from '@@/form-components/SwitchField';

import { DockerSecurityFormValues } from './types';

/**
 * Docker Security Policy form fields.
 * Pure field renderer without FormActions - used by both create and edit modes.
 */
export function DockerSecurityFormFields() {
  const { values, setFieldValue } =
    useFormikContext<DockerSecurityFormValues>();

  return (
    <FormSection title="Docker security settings">
      <SwitchField
        checked={!values.allowBindMountsForRegularUsers}
        onChange={(value) =>
          setFieldValue('allowBindMountsForRegularUsers', !value)
        }
        label="Hide bind mounts for non-administrators"
        tooltip="When enabled, bind mounts are hidden from regular users when creating containers, services, or stacks."
        labelClass="col-sm-7 col-lg-4"
        fieldClass="!mb-4"
        data-cy="disable-bind-mounts-switch"
      />
      <SwitchField
        checked={!values.allowPrivilegedModeForRegularUsers}
        onChange={(value) =>
          setFieldValue('allowPrivilegedModeForRegularUsers', !value)
        }
        label="Hide privileged mode for non-administrators"
        tooltip="When enabled, regular users will not be able to use privileged mode when creating containers."
        labelClass="col-sm-7 col-lg-4"
        fieldClass="!mb-4"
        data-cy="disable-privileged-mode-switch"
      />
      <SwitchField
        checked={!values.allowHostNamespaceForRegularUsers}
        onChange={(value) =>
          setFieldValue('allowHostNamespaceForRegularUsers', !value)
        }
        label="Hide the use of host PID 1 for non-administrators"
        tooltip="Prevent users from accessing the host filesystem through the host PID namespace."
        labelClass="col-sm-7 col-lg-4"
        fieldClass="!mb-4"
        data-cy="disable-host-namespace-switch"
      />
      <SwitchField
        checked={!values.allowStackManagementForRegularUsers}
        onChange={(value) =>
          setFieldValue('allowStackManagementForRegularUsers', !value)
        }
        label="Hide the use of Stacks for non-administrators"
        labelClass="col-sm-7 col-lg-4"
        fieldClass="!mb-4"
        data-cy="disable-stack-management-switch"
      />
      <SwitchField
        checked={!values.allowDeviceMappingForRegularUsers}
        onChange={(value) =>
          setFieldValue('allowDeviceMappingForRegularUsers', !value)
        }
        label="Hide device mappings for non-administrators"
        labelClass="col-sm-7 col-lg-4"
        fieldClass="!mb-4"
        data-cy="disable-device-mapping-switch"
      />
      <SwitchField
        checked={!values.allowContainerCapabilitiesForRegularUsers}
        onChange={(value) =>
          setFieldValue('allowContainerCapabilitiesForRegularUsers', !value)
        }
        label="Hide container capabilities for non-administrators"
        labelClass="col-sm-7 col-lg-4"
        fieldClass="!mb-4"
        data-cy="disable-container-capabilities-switch"
      />
      <SwitchField
        checked={!values.allowSysctlSettingForRegularUsers}
        onChange={(value) =>
          setFieldValue('allowSysctlSettingForRegularUsers', !value)
        }
        label="Hide sysctl settings for non-administrators"
        labelClass="col-sm-7 col-lg-4"
        fieldClass="!mb-4"
        data-cy="disable-sysctl-setting-switch"
      />
      <SwitchField
        checked={!values.allowSecurityOptForRegularUsers}
        onChange={(value) =>
          setFieldValue('allowSecurityOptForRegularUsers', !value)
        }
        label="Hide security-opt for non-administrators"
        tooltip="When enabled, regular users will not be able to use security-opt when creating containers."
        labelClass="col-sm-7 col-lg-4"
        fieldClass="!mb-4"
        data-cy="disable-security-opt-switch"
      />
    </FormSection>
  );
}
